/** Tab C — Department Performance report. */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, PieChart, Pie, Cell, Legend,
} from "recharts";
import { useDrillThrough } from "@/hooks/useDrillThrough";
import { COLORS } from "@/components/reports/reportTheme";
import { Building2 } from "lucide-react";

// =============== Tab C — Department Performance ===============
export function DeptPerformanceReport({ filters }) {
  const [data, setData] = useState(null);
  const drillTo = useDrillThrough();
  useEffect(() => {
    const params = { ...filters };
    Object.keys(params).forEach((k) => (params[k] === "all" || params[k] === "") && delete params[k]);
    api.get("/analytics/departments", { params }).then((r) => setData(r.data));
  }, [JSON.stringify(filters)]); // eslint-disable-line

  if (!data) return <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="dept-performance-report">
      <Card className="border-slate-200" data-testid="dept-scorecard-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-base flex items-center gap-1.5">
            <Building2 className="w-4 h-4 text-indigo-600" />Department scorecard
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="text-xs uppercase tracking-wider text-slate-500">
                <TableHead>Dept</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Active</TableHead>
                <TableHead className="text-right">Resolved</TableHead>
                <TableHead className="text-right">SLA %</TableHead>
                <TableHead className="text-right">MTTR</TableHead>
                <TableHead className="text-right">Oldest</TableHead>
                <TableHead>Top issues</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.scorecard.map((row) => (
                <TableRow key={row.dept} className="hover:bg-slate-50 cursor-pointer" data-testid={`scorecard-row-${row.dept}`}
                          onClick={() => drillTo("department_name", row.dept, { date_from: filters.date_from, date_to: filters.date_to })}
                          title="Open tickets for this department">
                  <TableCell className="text-sm font-medium text-slate-800">{row.dept}</TableCell>
                  <TableCell className="text-right text-sm">{row.total}</TableCell>
                  <TableCell className="text-right text-sm">{row.open + row.in_progress + row.pending_user}</TableCell>
                  <TableCell className="text-right text-sm">{row.resolved}</TableCell>
                  <TableCell className="text-right">
                    {row.sla_pct == null ? "—" : (
                      <Badge variant="outline" className={`text-[10px] ${row.sla_pct >= 95 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : row.sla_pct >= 80 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>
                        {row.sla_pct}%
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-xs font-mono text-slate-600">
                    {row.mttr_hours != null ? `${row.mttr_hours}h` : "—"}
                  </TableCell>
                  <TableCell className="text-right text-xs font-mono text-slate-600">
                    {row.oldest_age_hours > 0 ? `${row.oldest_age_hours}h` : "—"}
                  </TableCell>
                  <TableCell className="text-[10px] text-slate-500 max-w-xs truncate">
                    {row.top_issues.map((i) => i.issue).join(" · ")}
                  </TableCell>
                </TableRow>
              ))}
              {data.scorecard.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-sm text-slate-400 py-8">No data</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-slate-200" data-testid="dept-radar-card">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Department comparison (top 6)</CardTitle></CardHeader>
          <CardContent className="h-80">
            {data.radar.length === 0 ? (
              <div className="text-sm text-slate-400 py-8 text-center">No data</div>
            ) : (
              <ResponsiveContainer>
                <RadarChart data={[
                  { axis: "Volume", ...Object.fromEntries(data.radar.map((d) => [d.dept, d.volume])) },
                  { axis: "SLA %", ...Object.fromEntries(data.radar.map((d) => [d.dept, d.sla_pct])) },
                  { axis: "Speed (1/MTTR)", ...Object.fromEntries(data.radar.map((d) => [d.dept, d.mttr_speed])) },
                  { axis: "First-response", ...Object.fromEntries(data.radar.map((d) => [d.dept, d.fcr])) },
                  { axis: "Assignment", ...Object.fromEntries(data.radar.map((d) => [d.dept, d.assignment_rate])) },
                  { axis: "Low breach", ...Object.fromEntries(data.radar.map((d) => [d.dept, d.low_breach])) },
                ]}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="axis" tick={{ fontSize: 10, fill: "#64748b" }} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9, fill: "#94a3b8" }} />
                  {data.radar.map((d, i) => (
                    <Radar key={d.dept} name={d.dept} dataKey={d.dept}
                           stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]} fillOpacity={0.15} />
                  ))}
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                </RadarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="dept-funnel-card">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Resolution funnel (top 6 depts)</CardTitle></CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer>
              <BarChart data={data.funnel} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis dataKey="dept" type="category" tick={{ fontSize: 10, fill: "#64748b" }} width={120} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="created" fill="#4f46e5" name="Created" />
                <Bar dataKey="assigned" fill="#0ea5e9" name="Assigned" />
                <Bar dataKey="first_responded" fill="#9333ea" name="First-responded" />
                <Bar dataKey="resolved" fill="#059669" name="Resolved" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200" data-testid="backlog-age-card">
        <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Backlog age pyramid (active tickets)</CardTitle></CardHeader>
        <CardContent className="h-72">
          <ResponsiveContainer>
            <BarChart data={data.backlog_age}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="dept" tick={{ fontSize: 10, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="0-1d" stackId="a" fill="#059669" />
              <Bar dataKey="1-3d" stackId="a" fill="#0ea5e9" />
              <Bar dataKey="3-7d" stackId="a" fill="#d97706" />
              <Bar dataKey="7-14d" stackId="a" fill="#ea580c" />
              <Bar dataKey="14+d" stackId="a" fill="#e11d48" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}

